import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRtcSession } from '@/media/rtcSessionContext';
import {
  CameraIcon,
  ExpandIcon,
  LeaveIcon,
  MicIcon,
  MicOffIcon,
  PipIcon,
} from '@/components/ui/icons';
import { cn, initials } from '@/lib/utils';

function AudioSink({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = ref.current;
    if (!audio || audio.srcObject === stream) return;
    audio.srcObject = stream;
    void audio.play().catch(() => undefined);
  }, [stream]);

  return <audio ref={ref} autoPlay className="hidden" />;
}

/** Compact meeting controls shown while the user works elsewhere in the app. */
export function PersistentCallPlayer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    session,
    rtc,
    primaryStream,
    primaryLabel,
    primaryIsLocal,
    pipSupported,
    endSession,
    enterPictureInPicture,
  } = useRtcSession();
  const videoRef = useRef<HTMLVideoElement>(null);

  const sessionPath = session
    ? session.kind === 'meeting'
      ? `/meetings/${session.id}/room`
      : `/call/${session.id}`
    : '';
  const isFullRoom = Boolean(session && pathname === sessionPath);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || video.srcObject === primaryStream) return;
    video.srcObject = primaryStream;
    if (primaryStream) void video.play().catch(() => undefined);
  }, [primaryStream]);

  if (!session || isFullRoom) return null;

  const hasVideo = Boolean(
    primaryStream
      ?.getVideoTracks()
      .some((track) => track.readyState === 'live' && track.enabled),
  );
  const backgroundStreams = rtc.peers.flatMap((peer) =>
    [peer.streams.camera, peer.streams.screen].filter((stream): stream is MediaStream =>
      Boolean(stream && stream !== primaryStream),
    ),
  );

  return (
    <section
      aria-label={`Active ${session.kind}`}
      className="fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom))] right-4 z-40 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-slate-950 shadow-[0_20px_55px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
    >
      <button
        type="button"
        onClick={() => navigate(sessionPath)}
        className="group relative block aspect-video w-full overflow-hidden bg-slate-900 text-left"
        aria-label={`Return to ${session.title}`}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={primaryIsLocal}
          className={cn('h-full w-full object-cover', primaryIsLocal && 'scale-x-[-1]')}
        />
        {!hasVideo && (
          <span className="absolute inset-0 grid place-items-center bg-slate-900">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-slate-700 text-base font-semibold text-white">
              {initials(primaryLabel)}
            </span>
          </span>
        )}
        <span className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/85 to-transparent px-3 pb-2 pt-8">
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-white">
              {session.title}
            </span>
            <span className="block truncate text-xs text-slate-300">{primaryLabel}</span>
          </span>
          <ExpandIcon size="sm" className="shrink-0 text-white" />
        </span>
      </button>

      <div className="flex items-center gap-1.5 px-2 py-2">
        <button
          type="button"
          onClick={rtc.toggleMic}
          aria-label={rtc.micOn ? 'Mute microphone' : 'Unmute microphone'}
          className={cn(
            'grid h-9 w-9 place-items-center rounded-lg text-slate-200 hover:bg-white/10',
            !rtc.micOn && 'bg-red-500/20 text-red-200',
          )}
        >
          {rtc.micOn ? <MicIcon size="sm" /> : <MicOffIcon size="sm" />}
        </button>
        <button
          type="button"
          onClick={rtc.toggleCam}
          aria-label={rtc.camOn ? 'Turn camera off' : 'Turn camera on'}
          className={cn(
            'grid h-9 w-9 place-items-center rounded-lg text-slate-200 hover:bg-white/10',
            !rtc.camOn && 'text-slate-500',
          )}
        >
          <CameraIcon size="sm" />
        </button>
        {pipSupported && (
          <button
            type="button"
            onClick={() => void enterPictureInPicture()}
            aria-label="Open Picture-in-Picture"
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-200 hover:bg-white/10"
          >
            <PipIcon size="sm" />
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate(sessionPath)}
          className="ml-auto rounded-lg px-2.5 py-2 text-xs font-medium text-slate-200 hover:bg-white/10"
        >
          Return
        </button>
        <button
          type="button"
          onClick={endSession}
          aria-label={`Leave ${session.kind}`}
          className="grid h-9 w-10 place-items-center rounded-lg bg-red-600 text-white hover:bg-red-500"
        >
          <LeaveIcon size="sm" />
        </button>
      </div>
      {backgroundStreams.map((stream) => (
        <AudioSink key={stream.id} stream={stream} />
      ))}
    </section>
  );
}
